import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertCircle, Clock } from "lucide-react";
import Layout from "../components/Layout";
import StepIndicator from "../components/StepIndicator";
import BackButton from "../components/BackButton";
import Loading from "../components/Loading";
import PrimaryButton from "../components/PrimaryButton";
import { listarHorarios } from "../lib/api";
import { getBooking, setBooking } from "../lib/bookingState";
import { formatTime } from "../lib/format";
import ParticularWarningModal from "../components/ParticularWarningModal";

const STEPS = ["CPF", "Especialidade", "Profissional", "Horario", "Confirmar"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function firstDayOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function lastDayOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function dayKeyFromSlot(datetime) {
  return String(datetime || "").slice(0, 10);
}

function buildCalendarDays(monthDate) {
  const first = firstDayOfMonth(monthDate);
  const last = lastDayOfMonth(monthDate);

  const firstWeekDay = first.getDay(); // domingo=0
  const offset = firstWeekDay === 0 ? 6 : firstWeekDay - 1;

  const days = [];

  for (let i = 0; i < offset; i++) {
    days.push(null);
  }

  for (let day = 1; day <= last.getDate(); day++) {
    days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }

  return days;
}

function SlotButton({ slot, selected, onSelect }) {
  const isSel = selected?.datetime === slot.datetime;

  return (
    <button
      data-testid={`slot-${slot.datetime}`}
      onClick={() => onSelect(slot)}
      className={[
        "h-12 rounded-xl text-sm font-medium border transition-all",
        isSel
          ? "bg-[#00357C] text-white border-[#00357C] shadow-sm"
          : "bg-white text-slate-700 border-slate-200 hover:border-[#00357C]/40 hover:text-[#00357C]",
      ].join(" ")}
    >
      {formatTime(slot.datetime)}
    </button>
  );
}

export default function AgendarHorarios() {
  const navigate = useNavigate();
  const booking = getBooking();

  const [monthDate, setMonthDate] = useState(firstDayOfMonth(new Date()));
  const [slots, setSlots] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showParticularWarning, setShowParticularWarning] = useState(false);

  const dataStart = toISODate(firstDayOfMonth(monthDate));
  const dataEnd = toISODate(lastDayOfMonth(monthDate));

  useEffect(() => {
    if (!booking.profissional_id) {
      navigate("/agendar/profissional", { replace: true });
      return;
    }

    setSlots(null);
    setSelected(null);
    setSelectedDay(null);

    listarHorarios({
      profissionalId: booking.profissional_id,
      especialidadeId: booking.especialidade_id,
      dataStart,
      dataEnd,
      convenioId: booking.convenio_id,
    })
      .then((data) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthDate]);

  const slotsByDay = useMemo(() => {
    const map = {};

    for (const s of slots || []) {
      const key = dayKeyFromSlot(s.datetime);
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }

    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => String(a.datetime).localeCompare(String(b.datetime)));
    }

    return map;
  }, [slots]);

  const calendarDays = useMemo(() => buildCalendarDays(monthDate), [monthDate]);

  const selectedDaySlots = selectedDay ? slotsByDay[selectedDay] || [] : [];

  const temConvenio = Boolean(booking.convenio_id);

  const slotsConvenio = temConvenio
    ? selectedDaySlots.filter((s) => s.convenio)
    : [];

  const slotsParticular = temConvenio
    ? selectedDaySlots.filter((s) => !s.convenio)
    : selectedDaySlots;

  function selectDay(day) {
    const key = toISODate(day);
    if (!slotsByDay[key]) return;

    setSelectedDay(key);
    setSelected(null);
  }

  function confirm() {
    if (!selected) return;

    const isParticular = temConvenio && !selected.convenio;

    if (isParticular) {
      setShowParticularWarning(true);
      return;
    }

    avancarParaConfirmacao(false);
  }

  function avancarParaConfirmacao(forcarParticular) {
    setBooking({
      data_hora: selected.datetime,
      local_id: selected.local_id || 17,
      forcar_particular: forcarParticular,
    });

    navigate("/agendar/confirmar");
  }

  return (
    <Layout>
      <BackButton to="/agendar/profissional" />
      <StepIndicator steps={STEPS} current={4} />

      <div className="fade-up">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#00357C]">
          {booking.profissional_nome}
        </div>

        <h1
          className="text-2xl sm:text-3xl tracking-tight font-bold text-slate-900 mt-1"
          style={{ fontFamily: "Manrope" }}
        >
          Escolha um horário
        </h1>
      </div>

      <div className="mt-6 pb-32">
        <div className="bg-slate-50 rounded-3xl p-4 sm:p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => setMonthDate(addMonths(monthDate, -1))}
              className="w-11 h-11 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center text-slate-700"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}
            </div>

            <button
              type="button"
              onClick={() => setMonthDate(addMonths(monthDate, 1))}
              className="w-11 h-11 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center text-slate-700"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-2 mb-2">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-xs sm:text-sm text-slate-500 font-medium">
                {w}
              </div>
            ))}
          </div>

          {slots === null ? (
            <Loading label="Buscando horários do mês..." />
          ) : (
            <div className="grid grid-cols-7 gap-2" data-testid="calendario-horarios">
              {calendarDays.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} className="h-14 sm:h-16" />;
                }

                const key = toISODate(day);
                const available = Boolean(slotsByDay[key]?.length);
                const isSelected = selectedDay === key;

                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!available}
                    onClick={() => selectDay(day)}
                    className={[
                      "h-14 sm:h-16 rounded-xl text-base sm:text-lg font-medium transition-all border",
                      available
                        ? "bg-green-100 text-green-800 border-green-100 hover:bg-green-200"
                        : "bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed",
                      isSelected ? "ring-2 ring-[#00357C] bg-[#00357C] text-white" : "",
                    ].join(" ")}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {slots && slots.length === 0 && (
          <div className="mt-6 bg-amber-50 text-amber-800 border border-amber-100 rounded-2xl px-4 py-4 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" />
            <div>Nenhum horário disponível neste mês. Tente avançar para o próximo mês ou escolha outro profissional.</div>
          </div>
        )}

        {selectedDay && (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-slate-900 font-semibold" style={{ fontFamily: "Manrope" }}>
              <Clock size={17} />
              Horários disponíveis
            </div>

            {temConvenio ? (
              <>
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#3D8E92]">
                    Pelo seu convênio
                  </div>

                  {slotsConvenio.length === 0 ? (
                    <p className="text-sm text-slate-500 mt-2">
                      Nenhum horário disponível pelo seu convênio nesse dia.
                    </p>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slotsConvenio.map((s) => (
                        <SlotButton key={s.datetime} slot={s} selected={selected} onSelect={setSelected} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Particular
                  </div>

                  {slotsParticular.length === 0 ? (
                    <p className="text-sm text-slate-500 mt-2">
                      Nenhum horário particular disponível nesse dia.
                    </p>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slotsParticular.map((s) => (
                        <SlotButton key={s.datetime} slot={s} selected={selected} onSelect={setSelected} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slotsParticular.map((s) => (
                  <SlotButton key={s.datetime} slot={s} selected={selected} onSelect={setSelected} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur p-4">
        <div className="max-w-2xl mx-auto">
          <PrimaryButton onClick={confirm} disabled={!selected} data-testid="horario-continuar">
            {selected ? "Continuar com este horário" : "Selecione um horário"}
          </PrimaryButton>
        </div>
      </div>

      <ParticularWarningModal
        open={showParticularWarning}
        message="O horário escolhido não está vinculado ao convênio do seu cadastro."
        onCancel={() => setShowParticularWarning(false)}
        onConfirm={() => {
          setShowParticularWarning(false);
          avancarParaConfirmacao(true);
        }}
      />
    </Layout>
  );
}